//#region Imports
// ** React Imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import { IconButton, Box, Typography, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";
import VideoPlaceholderIcon from "images/video_placeholder.png";
import CSV_PlaceholderIcon from "images/Attachment_Icons/CSV__PlaceholderIcon.png";
import PDF_PlaceholderIcon from "images/Attachment_Icons/PDF_PlaceholderIcon.png";
import DOCX_PlaceholderIcon from "images/Attachment_Icons/DOCX__PlaceholderIcon.png";
import XLSX_PlaceholderIcon from "images/Attachment_Icons/XLSX__PlaceholderIcon.png";
import FilePlaceholder from "images/image_placeholder.png";

// ** Logic Imports
import { useInvoicePermission } from "../permissions/InvoicePermissions";

// ** API Imports
import { GetJobs } from "./GetJobs";
import { GetDepositWorkOrders } from "./GetDepositWO";
import { SaveInvoice, SaveAssessmentAmount } from "./SaveInvoice";
import { GetAssessmentAmount } from "./GetAssessmentAmount";
import { SaveAttachments } from "components/logic/SaveAttachments";
import { GetIndividualInvoice } from "./GetInvoice";
import { GetSalesTax } from "./GetSalesTax";
import { GetCompanyName } from "./GetCompanyName";
import { SaveSalesTax } from "screens/Settings/logic/CompanySettingsLogic";
import { GetAllJobs } from "screens/WorkOrders/logic/GetAllJobs";
import { GetWorkReqService } from "screens/Requests/logic/GetWorkRequirement";
import { useWorkRequirementLogic } from "screens/WorkOrders/logic/work-requirement-logic";
import { GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";

// ** Third-Party Imports
import dayjs from "dayjs";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
//#endregion Imports

export const useCreateInvoice = ({ toast, mode }) => {
  //#region Permissions
  const { InvoicePermissionProps } = useInvoicePermission();
  const { hasInvoiceSavePermission } = InvoicePermissionProps;
  //#endregion Permissions

  //#region Hooks and Constants
  const navigate = useNavigate();
  const theme = useTheme();
  const MySwal = withReactContent(Swal);
  const contentHeightFixed = false;
  //#endregion Hooks and Constants

  //#region States

  //#region Job Selection States
  const [invoiceType, setInvoiceType] = useState("Assessment");
  const [assessments, setAssessments] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [depositWorkOrders, setDepositWorkOrders] = useState([]);
  const [selectedID, setSelectedID] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [errors, setErrors] = useState({
    id: false,
    jobs: false,
  });
  const [requestDetails, setRequestDetails] = useState({});
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [serviceRequest, setServiceRequest] = useState({});
  //#endregion Job Selection States

  //#region Invoice Creation States
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState("1");
  const [openSelectJobsDlg, setOpenSelectJobsDlg] = useState(false);
  const [openAttachments, setOpenAttachments] = useState(false);
  const [openNotes, setOpenNotes] = useState(false);
  const [openTNC, setOpenTNC] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [filteredRows, setFilteredRows] = useState([]);
  const [openJobDetail, setOpenJobDetail] = useState(false);
  const [jobID, setJobID] = useState("");
  const [openGenerateInvoice, setOpenGenerateInvoice] = useState(false);
  const [workRequirements, setWorkRequirements] = useState({});
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [noteID, setNoteID] = useState(0);
  const [term, setTerm] = useState("");
  const [terms, setTerms] = useState([]);
  const [termID, setTermID] = useState(0);
  const [invoiceID, setInvoiceID] = useState(null);
  const [errorsDetails, setErrorsDetails] = useState({
    dueDate: false,
    paymentMethods: false,
    assessmentAmount: false,
    discountAmount: false,
  });
  const [salesTaxList, setSalesTaxList] = useState([]);
  const [jobDetail, setJobDetail] = useState();
  const [existingInvoice, setExistingInvoice] = useState({});
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [companyDetails, setCompanyDetails] = useState({});
  const [previewData, setPreviewData] = useState({});
  const [openCountyDlg, setOpenCountyDlg] = useState(false);
  const [rules, setRules] = useState({
    canEdit: true,
    canDelete: true,
    canSubmit: true,
    nextStatuses: [],
    cannotGoBack: false,
  });
  const [invoice, setInvoice] = useState({
    invoiceID: 0,
    requestID: 0,
    requestType: "",
    isDeposit: false,
    depositRequired: 0,
    invoiceTitle: "",
    invoiceType: "",
    dueDate: "",
    status: "",
    county: "",
    subTotal: 0,
    taxAmount: 0,
    discountAmount: 0,
    totalAmount: 0,
    depositUtilizedAmount: 0,
    notes: [],
    paymentMethods: [],
    additionalPaymentTerms: [],
    invoiceJobs: [],
    customerID: 0,
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    jobsiteName: "",
    jobsiteAddress: "",
    billingAddress_Street1: "",
    billingAddress_Street2: "",
    requestTitle: "",
    companyName: "",
  });

  // Invoice Type: ('Assessment','WorkOrder','WorkOrderDeposit')

  //#endregion Invoice Creation States

  //#endregion States

  //#region Handlers

  //#region General Handlers
  // useEffect(() => {
  //   setPreviewData({
  //     ...invoiceDetails,
  //     ...companyDetails,
  //   });
  // }, [invoiceDetails, companyDetails]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handlePageBack = () => {
    navigate("/invoices");
  };
  //#endregion General Handlers

  //#region Job Selection Handlers
  const fetchRecords = async () => {
    try {
      const response = await GetJobs(0);
      const responseDepWO = await GetDepositWorkOrders(0);

      if (!response.isError) {
        let records = response.result;
        let filteredAssessments = records.filter(
          (x) => x.entityType.toLowerCase() === "assessment"
        );
        let filteredWorkOrders = records.filter(
          (x) => x.entityType.toLowerCase() === "workorder"
        );
        setAssessments(filteredAssessments);
        setWorkOrders(filteredWorkOrders);
      }

      if (!responseDepWO.isError) {
        let depositWO = responseDepWO.result;
        setDepositWorkOrders(depositWO);
      }
    } catch (error) {
      console.error("Failed to fetch records:", error);
    }
  };

  const fetchSalesTax = async () => {
    try {
      const response = await GetSalesTax();

      if (!response.isError) {
        setSalesTaxList(response.result);
      }
    } catch (error) {
      console.error("Failed to fetch sales tax:", error);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await GetCompanyName();

      if (!response.isError) {
        setCompanyDetails(response.result);
      }
    } catch (error) {
      console.error("Failed to fetch sales tax:", error);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchSalesTax();
    fetchCompanyDetails();
  }, []);

  // useEffect(() => {
  //   setSelectedJobIds([]);

  //   if (!selectedID) {
  //     setJobs([]);
  //     setInvoice({});
  //   }
  // }, [selectedID]);

  useEffect(() => {
    console.log('invoice', invoice)
  }, [invoice])

  const allOptions = React.useMemo(() => {
    if (jobs?.length === 0) return [];

    return [
      "Select All",
      ...jobs.map((job) => ({
        id: job.jobID,
        label: `${job.industryName} - ${job.serviceName} (${dayjs(
          job.scheduleStart
        ).format("MM/DD/YYYY")})`,
      })),
    ];
  }, [jobs]);

  const selectedJobs = jobs
    .filter((job) => selectedJobIds.includes(job.jobID))
    .map((job, index) => ({
      ...job,
      id: index,
    }));

  const handleOpenSelectJobsDlg = () => setOpenSelectJobsDlg(true);
  const handleCloseSelectJobsDlg = () => setOpenSelectJobsDlg(false);

  const handleChangeSelectedID = (selectedItem) => {
    setSelectedID(selectedItem?.requestID || null);
    setJobs([]);
    setSelectedJobIds([]);
    setFilteredRows([]);

    setInvoice((prev) => ({
      invoiceID: prev.invoiceID || 0,
      preFix: selectedItem?.preFix,
      requestID: selectedItem?.requestID,
      requestType: selectedItem?.entityType,
      isDeposit: selectedItem?.isDeposit,
      depositRequired: selectedItem?.depositAmount,
      invoiceTitle: "",
      invoiceType: invoiceType || "",
      dueDate: prev.dueDate,
      status: prev.status || "",
      county: "",
      subTotal: 0,
      taxAmount: 0,
      salesTax: 0,
      discountAmount: 0,
      totalAmount: invoiceType === "Assessment" ? prev.assessmentAmount : invoiceType === 'WorkOrderDeposit' ? selectedItem?.depositAmount : 
                    handleTotalAmount(selectedItem?.jobs) || 0,
      depositUtilizedAmount: 0,
      notes: prev.notes || [],
      paymentMethods: prev.paymentMethods || [],
      additionalPaymentTerms: prev.additionalPaymentTerms || [],
      invoiceJobs: selectedItem?.jobs || [],
      customerID: selectedItem?.customerID,
      customerName: selectedItem?.customerName || "",
      customerEmail: selectedItem?.email || "",
      customerPhone: selectedItem?.customerPhone || "",
      jobsiteName: selectedItem?.jobSiteName || "",
      jobsiteAddress: selectedItem?.jobSiteAddress || "",
      billingAddress_Street1: selectedItem?.billingAddress || "",
      billingAddress_Street2: "",
      requestTitle: selectedItem?.requestTitle || "",
      companyName: prev.companyName,
    }));
    if(selectedItem?.entityType === 'WorkOrder'){
      setJobs(selectedItem?.jobs)
    }
  };

  const handleSelectedJobIDs = (newValue) => {
    const selectAllPresent = newValue.some(
      (opt) => opt === "Select All"
    );

    if (selectAllPresent) {
      setSelectedJobIds(
        selectedJobIds.length === jobs.length
          ? []
          : jobs.map((job) => job.jobID)
      );
      return;
    }
    const newSelectedIds = newValue.map((opt) => opt.id);
    setSelectedJobIds(newSelectedIds);

      setInvoice((prev) => {
        const existingJobsMap = new Map();
        prev.invoiceJobs?.forEach(job => {
          existingJobsMap.set(job.jobID, job);
        });

        const newInvoiceJobs = newValue.map((opt) => {
          const jobId = opt.id;
          const existingJob = existingJobsMap.get(jobId);
          
          return {
            invoiceJobID: existingJob?.invoiceJobID || 0,
            jobID: jobId,
            billedAmount: existingJob?.billedAmount || 0,
            taxRate: existingJob?.taxRate || 0,
            taxAmount: existingJob?.taxAmount || 0
          };
        });

        return {
          ...prev,
          invoiceJobs: newInvoiceJobs,
        };
      });
  };

  const handleValidation = () => {
    const newErrors = {
      id: false,
      jobs: false,
    };

    if (invoiceType === "Assessment" || invoiceType === "WorkOrderDeposit") {
      if (!selectedID || selectedID === 0) {
        newErrors.id = true;
      }
    } else {
      if (!selectedID || selectedID === 0) {
        newErrors.id = true;
      }
      if (selectedJobs.length === 0) {
        newErrors.jobs = true;
      }
    }

    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  const handleSaveJobs = () => {
    const isValid = handleValidation();
    if (isValid) {
      let updatedInvoiceDetails = {};

      if(invoiceType === 'WorkOrder'){
        updatedInvoiceDetails = {
          ...invoice,
          invoiceJobs:
            invoice?.invoiceJobs?.filter((job) =>
              selectedJobIds.includes(job.jobID)
            ) || [],
          invoiceID: invoice.invoiceID,
          dueDate: invoice.dueDate,
          companyName: invoice.companyName,
          status: invoice.status,
        };
      } else{
        updatedInvoiceDetails = {
          ...invoice,
          invoiceID: invoice.invoiceID,
          dueDate: invoice.dueDate,
          companyName: invoice.companyName,
          status: invoice.status,
        };
      }
      
      setInvoice(updatedInvoiceDetails);
      if (invoiceType === "Assessment") {
        fetchAssessmentAmount(invoice.requestID);
      }

      if(invoiceType === "WorkOrder"){
        setFilteredRows(selectedJobs);
      }
      handleCloseSelectJobsDlg();
    } else {
      toast.error("Please fill all required fields.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "15px",
        },
      });
    }
  };
  //#endregion Job Selection Handlers

  //#region Invoice Creation Handlers

  const fetchInvoice = async (invID) => {
    setLoading(true);
    try {
      const response = await GetIndividualInvoice(invID, navigate);

      if (!response.isError) {
        const invoiceData = response.result[0];
        const requestID = invoiceData.requestID;
        const invoiceType = invoiceData.invoiceType;
        const invoiceID = invoiceData.invoiceID;

        await fetchRecords();
        
        setInvoice(() => ({
          ...invoiceData,
          salesTax: invoiceData.invoiceJobs?.[0]?.taxRate
        }));
        setExistingInvoice(() => ({
          ...invoiceData,
          salesTax: invoiceData.invoiceJobs?.[0]?.taxRate
        }));
        // setExistingInvoice(JSON.parse(JSON.stringify(invoiceData)));
        setSelectedID(requestID);
        setInvoiceID(invoiceID);
        setInvoiceType(invoiceType);

        if(invoiceType === "Assessment"){
          await fetchAssessmentAmount(requestID);
        }

        if (invoiceType === "WorkOrder") {
          const selectedJobIDs = invoiceData?.invoiceJobs?.map(
            (job) => job.jobID
          ) || [];
          
          setSelectedJobIds(selectedJobIDs);
          await fetchWorkOrderJobs(requestID, selectedJobIDs);
        }

        const notes = invoiceData.notes || [];
        const maxNoteID =
          notes.length > 0
            ? Math.max(...notes.map((note) => note.noteID || 0))
            : 0;
        setNoteID(maxNoteID);
        setNotes(
          notes.map((note, index) => ({
            ...note,
            id: note.noteID,
          }))
        );

        const terms = invoiceData.additionalPaymentTerms || [];
        const maxTermID =
          terms.length > 0
            ? Math.max(...terms.map((term) => term.paymentTermID || 0))
            : 0;
        setTerms(
          terms.map((term, index) => ({
            ...term,
            id: term.paymentTermID,
          }))
        );
        setTermID(maxTermID);
      }
    } catch (error) {
      console.error("Error fetching invoice: ", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrderJobs = async (requestID, selectedJobIDs) => {
    try {
      const response = await GetJobs(requestID);

      if (!response.isError) {
        const workOrder = response.result[0];

        const workOrderJobs = workOrder?.jobs || [];
        setJobs(workOrderJobs);

        const filteredJobs = workOrderJobs?.filter(job => 
          selectedJobIDs.includes(job.jobID)
        ) || [];
        setFilteredRows(filteredJobs?.map((job) => ({
          ...job,
          id: job.jobID
        })) || []);
        // setInvoice((prev) => ({
        //   ...prev,
        //   invoiceJobs: filteredJobs
        // }))
        // setExistingInvoice((prev) => ({
        //   ...prev,
        //   invoiceJobs: filteredJobs
        // }))
      }
    } catch (error) {
      console.error("Failed to fetch records:", error);
    }
  };

  const handleOpenGenerateInvoice = () => {
    const validate = validateFields();
    if (!validate) {
      toast.error("Please fill all required fields", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "15px",
        },
      });
    } else {
      setPreviewData({
        ...invoice,
        ...companyDetails,
      });
      setOpenGenerateInvoice(true);
    }
  };
  const handleCloseGenerateInvoice = () => setOpenGenerateInvoice(false);

  const handleOpenCountyDlg = () => {
    setOpenCountyDlg(true);
  };
  const handleCloseCountyDlg = () => {
    setOpenCountyDlg(false);
  };
  const handleAddCounty = async (tax) => {
    await SaveSalesTax(tax);
    fetchSalesTax();
  };

  const handleChangeInvoice = (name, value) => {
    let processedValue = value;

    if (name === "paymentMethods") {
      processedValue = Array.isArray(value) ? value : [value];
    } else if (
      [
        "assessmentAmount",
        "depositAmount",
        "discountAmount",
        "depositUtilizedAmount",
        "subTotal",
        "taxAmount",
        "totalAmount",
        "billedAmount",
      ].includes(name)
    ) {
      if (typeof value === "number") {
        processedValue = value;
      } else {
        let numericValue = String(value).replace(/[^\d.]/g, "");

        const decimalParts = numericValue.split(".");
        if (decimalParts.length > 2) {
          numericValue = `${decimalParts[0]}.${decimalParts.slice(1).join("")}`;
        }
        numericValue = numericValue.replace(/^0+(\d)/, "$1");

        processedValue = numericValue === "" ? "0" : numericValue;
      }
    } else if (
      ["invoiceDate", "dueDate", "createDate", "modifiedDate"].includes(name)
    ) {
      processedValue = value;
    } else if (
      ["notes", "additionalPaymentTerms", "invoiceJobs"].includes(name)
    ) {
      processedValue = Array.isArray(value) ? value : [value];
    } else if (["isActive"].includes(name)) {
      processedValue = Boolean(value);
    } else {
      processedValue = value;
    }

    setInvoice((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleChangeInvoiceDetails = (name, value) => {
    let processedValue = value;

    if (name === "paymentMethods") {
      processedValue = Array.isArray(value) ? value : [value];
    } else if (
      ["assessmentAmount", "discountAmount", "depositUtilizedAmount"].includes(
        name
      )
    ) {
      if (typeof value === "number") {
        processedValue = value;
      } else {
        let numericValue = String(value).replace(/[^\d.]/g, "");

        const decimalParts = numericValue.split(".");
        if (decimalParts.length > 2) {
          numericValue = `${decimalParts[0]}.${decimalParts.slice(1).join("")}`;
        }
        numericValue = numericValue.replace(/^0+(\d)/, "$1");

        processedValue = numericValue === "" ? "0" : numericValue;
      }
    }

    setInvoice((prev) => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const { WorkRequirementsProps } = useWorkRequirementLogic({
    requestID: 0,
    navigate,
    toast,
    setLoading,
    serviceRequest,
  });

  const { fetchWorkRequirements } = WorkRequirementsProps;
  //#endregion Props Destructuring

  //#region Handlers

  const handleOpenJobDetail = async (
    requestID,
    workReqID,
    jobID,
    customerID
  ) => {
    const serviceReq = await GetServiceRequests(customerID, requestID, 0);
    const workRequirementList = await GetWorkReqService(requestID, 0, navigate);

    const workReq = workRequirementList?.find(
      (wr) => wr.workReqID === workReqID
    );
    const job = workReq?.jobs.find((j) => j.jobID === jobID);

    setServiceRequest(serviceReq);
    setSelectedWorkReq(workReq);
    setSelectedJob(job);
    setOpenJobDetail(true);
    // fetchJobs();
  };

  const handleCloseJobDetail = () => {
    setOpenJobDetail(false);
    setSelectedWorkReq(null);
    setSelectedJob(null);
  };

  const removeJobConfirmation = (jobID) => {
    MySwal.fire({
      title: "<strong>Are you sure?</strong>",
      html: "<span>You won't be able to revert this!</span>",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      width: "390px",
      padding: "1rem",
      focusConfirm: false,
      showCloseButton: true,
      closeButtonHtml: "&times;",
      customClass: {
        popup:
          theme.palette.mode === "dark"
            ? "custom-swal-popup-dark"
            : "custom-swal-popup",
        title:
          theme.palette.mode === "dark"
            ? "custom-swal-title-dark"
            : "custom-swal-title",
        htmlContainer: "custom-swal-html",
        confirmButton: "custom-swal-confirm",
        cancelButton:
          theme.palette.mode === "dark"
            ? "custom-swal-cancel-dark"
            : "custom-swal-cancel",
        closeButton: "custom-swal-close",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        handleRemoveJob(jobID);
      }
    });
  };

  const handleRemoveJob = (jobID) => {
    setFilteredRows((prevRows) =>
      prevRows.filter((row) => row.jobID !== jobID)
    );
    setSelectedJobIds((prevIds) => prevIds.filter((id) => id !== jobID));
    setInvoice((prevDetails) => ({
      ...prevDetails,
      invoiceJobs: prevDetails.invoiceJobs.filter((job) => job.jobID !== jobID),
    }));
  };

  const handleOpenUploadDlg = () => setOpenUploadDlg(true);
  const handleCloseUploadDlg = () => {
    setOpenUploadDlg(false);
    setUploadFiles([]);
  };

  const fetchThumbnail = (file, setPreview) => {
    const mimeType = file.type;

    if (mimeType.startsWith("video/")) {
      setPreview(VideoPlaceholderIcon);
    } else if (mimeType.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else if (mimeType.startsWith("text/")) {
      setPreview(CSV_PlaceholderIcon);
    } else if (mimeType.startsWith("application/pdf")) {
      setPreview(PDF_PlaceholderIcon);
    } else if (mimeType.startsWith("application/msword")) {
      setPreview(DOCX_PlaceholderIcon);
    } else if (mimeType.startsWith("application/vnd")) {
      setPreview(XLSX_PlaceholderIcon);
    } else {
      setPreview(FilePlaceholder);
    }
  };

  const FileThumbnail = ({ file }) => {
    const [preview, setPreview] = useState(null);

    useEffect(() => {
      fetchThumbnail(file, setPreview);
    }, [file]);

    return (
      <img
        src={preview || "/images/ImageLoader.gif"}
        alt={file.name}
        width="50"
        height="50"
      />
    );
  };

  const handleRemoveAttachment = (name) => {
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.name !== name)
    );
  };

  const handleUploadAttachment = async (invoiceID, file) => {
    setLoading(true);
    try {
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("EntityType", "Invoice");
      formData.append("EntityID", invoiceID);
      formData.append("MediaType", "Attachment");

      let response = await SaveAttachments(formData, navigate);
      console.log("response", response);
    } catch (error) {
      console.error(
        "An error occurred while uploading the file. Please try again.",
        error
      );
    }
  };

  const handleAddNote = (method) => {
    const trimmedNote = note?.trim();
    if (!trimmedNote) return;

    if (method === "create") {
      setNoteID((prev) => prev + 1);
      setNotes((prev) => [
        ...prev,
        {
          id: noteID,
          noteID: 0,
          description: trimmedNote,
        },
      ]);
    } else if (method === "edit") {
      setNoteID((prev) => prev + 1);
      setNotes((prev) => [
        ...prev,
        {
          id: noteID + 1,
          noteID: 0,
          description: trimmedNote,
        },
      ]);
      setInvoice((prev) => ({
        ...prev,
        notes: [
          ...prev.notes,
          {
            id: noteID + 1,
            noteID: 0,
            description: trimmedNote,
          },
        ],
      }));
    }
    setNote("");
  };

  const handleDeleteNote = (noteID, method) => {
    if (method === "create") {
      setNotes((prevNotes) =>
        prevNotes.filter((note) => note.noteID !== noteID)
      );
    } else if (method === "edit") {
      setNotes((prevNotes) =>
        prevNotes.filter((note) =>
          "id" in note ? note.id !== noteID : note.noteID !== noteID
        )
      );
      setInvoice((prevDetails) => ({
        ...prevDetails,
        notes: prevDetails.notes.filter((note) =>
          "id" in note ? note.id !== noteID : note.noteID !== noteID
        ),
      }));
    }
  };

  const handleAddTerm = (method) => {
    const trimmedTerm = term?.trim();
    if (!trimmedTerm) return;

    if (method === "create") {
      setTermID((prev) => prev + 1);
      setTerms((prev) => [
        ...prev,
        {
          id: termID,
          paymentTermID: 0,
          paymentTerms: trimmedTerm,
          isActive: true,
        },
      ]);
    } else if (method === "edit") {
      setTermID((prev) => prev + 1);
      setTerms((prev) => [
        ...prev,
        {
          id: termID + 1,
          paymentTermID: 0,
          paymentTerms: trimmedTerm,
          isActive: true,
        },
      ]);
      setInvoice((prev) => ({
        ...prev,
        additionalPaymentTerms: [
          ...prev.additionalPaymentTerms,
          {
            id: termID + 1,
            paymentTermID: 0,
            paymentTerms: trimmedTerm,
            isActive: true,
          },
        ],
      }));
    }
    setTerm("");
  };

  const handleChangeTerm = (isChecked, ID, method) => {
    if (method === "create") {
      setTerms((prevTerms) =>
        prevTerms.map((term) =>
          term.id === ID ? { ...term, isActive: isChecked } : term
        )
      );
    } else if (method === "edit") {
      setTerms((prevTerms) =>
        prevTerms.map((term) =>
          ("id" in term ? term.id === ID : term.paymentTermID === ID)
            ? { ...term, isActive: isChecked }
            : term
        )
      );
      setInvoice((prevDetails) => ({
        ...prevDetails,
        additionalPaymentTerms: prevDetails.additionalPaymentTerms.map((term) =>
          ("id" in term ? term.id === ID : term.paymentTermID === ID)
            ? { ...term, isActive: isChecked }
            : term
        ),
      }));
    }
  };

  const handleDeleteTerm = (termID, method) => {
    if (method === "create") {
      setTerms((prevTerms) => prevTerms.filter((term) => term.id !== termID));
    } else if (method === "edit") {
      setTerms((prevTerms) =>
        prevTerms.filter((term) =>
          "id" in term ? term.id !== termID : term.paymentTermID !== termID
        )
      );
      setInvoice((prevDetails) => ({
        ...prevDetails,
        additionalPaymentTerms: prevDetails.additionalPaymentTerms.filter(
          (term) =>
            "id" in term ? term.id !== termID : term.paymentTermID !== termID
        ),
      }));
    }
  };

  const transformToApiPayload = (currentData) => {
    const paymentMethods = currentData.invoice.paymentMethods || null;

    const additionalPaymentTerms =
      currentData.terms && currentData.terms.length > 0
        ? currentData.terms.map((term) => ({
            paymentTermID: 0,
            paymentTerms: term.paymentTerms,
            isActive: term.isActive,
          }))
        : [];

    const notes =
      currentData.notes && currentData.notes.length > 0
        ? currentData.notes.map((note) => ({
            noteID: note.noteID || 0,
            description: note.description,
          }))
        : [];
        debugger
    const invoiceJobs =
      invoiceType === "WorkOrder"
        ? (
            // currentData.invoice.invoiceJobs || []
            selectedJobs || []
          ).map((job, index, array) => {
            const existingInvoiceJob = currentData.invoice.invoiceJobs?.find(
              invoiceJob => invoiceJob.jobID === job.jobID
            );

            const billedAmount = job.totalAmount || 0;
            const taxAmount =
              billedAmount * (parseFloat(currentData.invoice.salesTax) / 100) || 0;

            return {
              invoiceJobID: existingInvoiceJob?.invoiceJobID || 0,
              jobID: job.jobID,
              billedAmount: billedAmount,
              taxRate: currentData.invoice.salesTax || 0,
              taxAmount: taxAmount,
            };
          })
        : invoiceType === "WorkOrderDeposit"
        ? [
            {
              invoiceJobID: currentData.invoice.invoiceJobs?.[0]?.invoiceJobID || 0,
              jobID: currentData.invoice.invoiceJobs?.[0]?.jobID,
              billedAmount: parseFloat(
                currentData.invoice.subTotal ||
                  currentData.invoice.depositRequired ||
                  0
              ),
              taxRate: currentData.invoice.salesTax || 0,
              taxAmount: currentData.invoice.subTotal
                ? parseFloat(currentData.invoice.subTotal) *
                    (parseFloat(currentData.invoice.salesTax || 0) / 100) || 0
                : parseFloat(currentData.invoice.depositAmount) *
                    (parseFloat(currentData.invoice.salesTax || 0) / 100) || 0,
            },
          ]
        : [
            {
              invoiceJobID: currentData.invoice.invoiceJobs?.[0]?.invoiceJobID || 0,
              jobID: workRequirements?.jobs?.[0]?.jobID,
              billedAmount: parseFloat(
                currentData.invoice.assessmentAmount || 0
              ),
              taxRate: currentData.invoice.salesTax || 0,
              taxAmount:
                parseFloat(currentData.invoice.assessmentAmount) *
                  (parseFloat(currentData.invoice.salesTax || 0) / 100) || 0,
            },
          ];
    const discountAmount = parseFloat(currentData.invoice.discountAmount) || 0;

    let totalBilledAmount = invoiceJobs.reduce(
      (sum, job) => sum + (job.billedAmount || 0),
      0
    );
    const totalTaxAmount =
      totalBilledAmount * ((currentData.invoice.salesTax || 0) / 100);
    totalBilledAmount = totalBilledAmount + totalTaxAmount;

    if (currentData.invoice.depositUtilizedAmount > totalBilledAmount) {
      return;
    }

    const status =
      currentData.invoice.depositUtilizedAmount === 0
        ? "Draft"
        : currentData.invoice.depositUtilizedAmount < totalBilledAmount &&
          currentData.invoice.depositUtilizedAmount > 0
        ? "Partially Paid"
        : currentData.invoice.depositUtilizedAmount == totalBilledAmount
        ? "Paid"
        : "Draft";

    return {
      invoiceID: currentData.invoice.invoiceID || 0,
      requestID: currentData.invoice.requestID,
      invoiceTitle: currentData.invoice.invoiceTitle || "",
      invoiceType: invoiceType,
      invoiceDate: new Date().toISOString(),
      dueDate: new Date(currentData.invoice.dueDate).toISOString(),
      status: currentData.invoice.status || status,
      county: currentData.invoice.county,
      discountAmount,
      depositUtilizedAmount: currentData.invoice.depositUtilizedAmount || 0,
      notes,
      paymentMethods,
      additionalPaymentTerms,
      invoiceJobs,
    };
  };

  const fetchAssessmentAmount = async (requestID) => {
    try {
      const response = await GetAssessmentAmount(requestID);
      if (!response.isError) {
        debugger
        const WR = response.result;
        // const filteredWR = WR.filter((x) => x.subServiceID === 0);
        setWorkRequirements(WR[0]);
        const assessmentAmount = WR[0]?.jobs[0]?.flatRate || 0;
        handleChangeInvoiceDetails("assessmentAmount", assessmentAmount);
        setExistingInvoice((prev) => ({
          ...prev,
          assessmentAmount: assessmentAmount,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch Assessment Amount :", error);
    }
  };

  const handleSaveAssessmentAmount = async (updatedWorkRequirements) => {
    try {
      const responseWR = await SaveAssessmentAmount(
        updatedWorkRequirements,
        navigate
      );
      return responseWR;
    } catch (error) {
      console.error(error);
    }
  };

  const handleTotalAmount = (jobs) => {
    let totalAmount = jobs?.reduce((sum, job) => sum + job.totalAmount, 0);

    return totalAmount;
  };

  const validateFields = () => {
    const isAssessment = invoice.requestType === "Assessment";

    const isDepositWO = invoice?.isDeposit;

    const dueDate = new Date(invoice?.dueDate).toISOString();

    const newErrors = {
      dueDate: !dueDate,
      assessmentAmount: isAssessment
        ? !invoice.assessmentAmount ||
          isNaN(Number(invoice.assessmentAmount)) ||
          Number(invoice.assessmentAmount) <= 0
        : false,
      paymentMethods:
        !invoice.paymentMethods ||
        !Array.isArray(invoice.paymentMethods) ||
        invoice.paymentMethods.length === 0,
      discountAmount:
        invoice.discountAmount !== undefined &&
        invoice.discountAmount !== null &&
        !isNaN(Number(invoice.discountAmount)) &&
        Number(invoice.discountAmount) >
          (isAssessment
            ? Math.max(0, Number(invoice.assessmentAmount) || 0)
            : !isAssessment && isDepositWO
            ? Math.max(0, Number(invoice.depositAmount) || 0)
            : Math.max(
                0,
                Number(handleTotalAmount(invoice?.invoiceJobs)) || 0
              )),
    };

    const isError = Object.values(newErrors).some((error) => error);

    if (isError) {
      setTabValue("1");
    }

    setErrorsDetails(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSaveInvoice = async () => {
    const isValid = validateFields();
    if (!isValid || !hasInvoiceSavePermission) return;

    setLoading(true);

    try {
      const apiPayload = transformToApiPayload({
        invoice: invoice,
        notes,
        terms,
      });

      const responseInvoice = await SaveInvoice(apiPayload, navigate);

      if (responseInvoice.isError) {
        toast.error(responseInvoice.message || "Failed to save the Invoice", {
          containerId: "invoice-toasts",
        });
        return;
      }

      if (
        (invoice.requestType === "Assessment") &&
        invoice.assessmentAmount
      ) {
        const updatedWorkRequirements = {
          ...workRequirements,
          jobs: workRequirements.jobs.map((job) => ({
            ...job,
            flatRate: invoice.assessmentAmount,
            assignees: job.assignees
              ? job.assignees.map((assignee) => assignee.employeeID)
              : null,
            status: "unscheduled",
          })),
        };

        setWorkRequirements(updatedWorkRequirements);
        try {
          const responseWR = await handleSaveAssessmentAmount(
            updatedWorkRequirements
          );

          if (responseWR?.isError) {
            toast.error(
              "Invoice saved but assessment amount failed to update."
            );
          }
        } catch (error) {
          console.error("Assessment amount save error: ", error);
          toast.error("Invoice saved but assessment amount failed to update.", {
            style: {
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "15px",
            },
          });
        }
      }

      if (attachments && attachments.length > 0) {
        try {
          for (const attachment of attachments) {
            try {
              const responseAttachment = await handleUploadAttachment(
                responseInvoice.result.invoiceID,
                attachment
              );

              if (responseAttachment?.error) {
                toast.error(
                  `Failed to upload: ${attachment.name || attachment.path}`,
                  {
                    style: {
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: "15px",
                    },
                  }
                );
              }
            } catch (error) {
              console.error(
                `Error uploading ${attachment.name || attachment.path}:`,
                error
              );
              toast.error(
                `Failed to upload: ${attachment.name || attachment.path}`,
                {
                  style: {
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: "15px",
                  },
                }
              );
            }
          }
        } catch (error) {
          console.error("Unexpected error during uploads:", error);
        }
      }

      if (invoiceID === responseInvoice.result.invoiceID) {
        fetchInvoice(responseInvoice.result.invoiceID);
        toast.success("Invoice updated successfully!", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "15px",
          },
        });
      } else {
        resetStates();
        navigate(`/invoices/detail/${responseInvoice.result.invoiceID}`);
      }
    } catch (error) {
      console.error("Error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const resetStates = () => {
    setInvoiceID(null);
    setAssessments([]);
    setWorkOrders([]);
    setSelectedID(0);
    setJobs([]);
    setSelectedJobIds([]);
    setErrors({
      id: false,
      jobs: false,
    });
    setRequestDetails({});
    setFilteredRows([]);
    setJobID("");
    setInvoice({});
    setWorkRequirements({});
    setUploadFiles([]);
    setAttachments([]);
    setNote("");
    setNotes([]);
    setNoteID(0);
    setTerm("");
    setTerms([]);
    setTermID(0);
    setPreviewData([]);
    setErrorsDetails({
      dueDate: false,
      paymentMethods: false,
      assessmentAmount: false,
      discountAmount: false,
    });
  };
  //#endregion Invoice Creation Handlers

  //#endregion Handlers

  //#region Column Configurations
  const columns = [
    {
      flex: 0.1,
      field: "jobID",
      minWidth: 120,
      headerName: "Job ID",
      renderCell: ({ row }) => {
        const { jobID, workReqID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={() =>
                handleOpenJobDetail(
                  invoice.requestID,
                  workReqID,
                  jobID,
                  invoice.customerID
                )
              }
            >
              {jobID}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 180,
      field: "serviceName",
      headerName: "Service",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body2" sx={{ color: "text.primary" }}>
                {`${row.industryName} - ${row.serviceName}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 160,
      field: "bundleName",
      headerName: "Bundle Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography noWrap variant="body1" sx={{ color: "text.primary" }}>
              {row.bundleName ? row.bundleName : "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 250,
      field: "propertyAreaNames",
      headerName: "Property Area",
      renderCell: (params) => {
        const { row } = params;
        let propertyAreas =
          (row.propertyAreaNames || []).length > 0
            ? row.propertyAreaNames.join(", ")
            : "N/A";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body2" sx={{ color: "text.primary" }}>
                {propertyAreas}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 200,
      field: "actualStartDate",
      headerName: "Start Date",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body2" sx={{ color: "text.primary" }}>
                {dayjs(row.actualStartDate).format("MM/DD/YYYY")}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 180,
      field: "actualEndDate",
      headerName: "End Date",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body2" sx={{ color: "text.primary" }}>
                {dayjs(row.actualEndDate).format("MM/DD/YYYY")}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 140,
      field: "totalAmount",
      headerName: "Total Price",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography noWrap variant="body2" sx={{ color: "text.primary" }}>
                {`$${(Number(row.totalAmount) || 0).toFixed(2)}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      headerName: "Action",
      renderCell: (params) => {
        const { row } = params;
        return (
          <Tooltip title="Remove">
            <IconButton
              color="error"
              disabled={selectedJobIds.length <= 1}
              {...(invoiceID && invoiceID !== 0
                ? {
                    onClick: () => removeJobConfirmation(row.jobID),
                  }
                : {
                    onClick: () => handleRemoveJob(row.jobID),
                  })}
            >
              <Icon icon="tabler:trash" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];
  //#endregion Column Configurations

  //#region Props
  const JobSelectionProps = {
    handleOpenSelectJobsDlg,
    handleCloseSelectJobsDlg,
    handleChangeSelectedID,
    invoiceType,
    setInvoiceType,
    selectedID,
    setSelectedID,
    jobs,
    setJobs,
    selectedJobIds,
    setSelectedJobIds,
    allOptions,
    selectedJobs,
    assessments,
    workOrders,
    depositWorkOrders,
    handleSaveJobs,
    errors,
    handleSelectedJobIDs,
  };

  const InvoiceCreationProps = {
    contentHeightFixed,
    theme,
    loading,
    setLoading,
    theme,
    navigate,
    openSelectJobsDlg,
    setOpenSelectJobsDlg,
    openAttachments,
    setOpenAttachments,
    handleRemoveAttachment,
    openNotes,
    setOpenNotes,
    openTNC,
    setOpenTNC,
    showInvoiceDetails,
    setShowInvoiceDetails,
    filteredRows,
    setFilteredRows,
    openJobDetail,
    setOpenJobDetail,
    jobID,
    setJobID,
    openGenerateInvoice,
    setOpenGenerateInvoice,
    handlePageBack,
    handleOpenGenerateInvoice,
    handleCloseGenerateInvoice,
    handleCloseJobDetail,
    columns,
    invoice,
    setInvoice,
    openUploadDlg,
    setOpenUploadDlg,
    uploadFiles,
    setUploadFiles,
    handleOpenUploadDlg,
    handleCloseUploadDlg,
    attachments,
    setAttachments,
    selectedWorkReq,
    setSelectedWorkReq,
    selectedJob,
    setSelectedJob,
    fetchWorkRequirements,
    fetchRecords,
    rules,
    note,
    setNote,
    handleAddNote,
    notes,
    handleDeleteNote,
    term,
    setTerm,
    handleAddTerm,
    terms,
    handleDeleteTerm,
    handleChangeTerm,
    requestDetails,
    setRequestDetails,
    handleChangeInvoiceDetails,
    handleSaveInvoice,
    navigate,
    previewData,
    setPreviewData,
    errorsDetails,
    fetchInvoice,
    existingInvoice,
    setExistingInvoice,
    handleChangeInvoice,
    handleTabChange,
    tabValue,
    salesTaxList,
    jobDetail,
    FileThumbnail,
    handleOpenCountyDlg,
    handleCloseCountyDlg,
    openCountyDlg,
    handleAddCounty,
  };
  //#endregion Props

  return {
    JobSelectionProps,
    InvoiceCreationProps,
  };
};
